import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import incomingLongSound from 'assets/audio/call/start-call-long-incoming.mp3';
import orbitalLongSound from 'assets/audio/call/start-call-long-orbital.mp3';
import stepsLongSound from 'assets/audio/call/start-call-long-steps.mp3';
import chakraShortSound from 'assets/audio/call/start-call-short-chakra.mp3';
import chocolateShortSound from 'assets/audio/call/start-call-short-chocolate.mp3';
import tadaShortSound from 'assets/audio/call/start-call-short-tada.mp3';

const CALL_SOUND_FILES: Record<string, string> = {
  'incoming-long': incomingLongSound,
  'orbital-long': orbitalLongSound,
  'steps-long': stepsLongSound,
  'chakra-short': chakraShortSound,
  'chocolate-short': chocolateShortSound,
  'tada-short': tadaShortSound,
};

import { FormErrorHelperText, Info, MyRoomLink, Spinner } from 'components';
import { isHipaaLink } from 'config/productProfile';
import { useAppDispatch, useAppSelector } from 'store';
import { ApiClinicUser, ApiUser } from 'types';
import { IApiRequests, useApiRequests } from 'hooks';
import { getUser, selectUser } from 'store/user';

import styles from './RoomSettingsForm.module.scss';

export function RoomSettingsForm() {
  const { t, i18n } = useTranslation('settings');
  const isRtl = i18n.dir() === 'rtl';
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const user: ApiUser = useAppSelector(selectUser)!;
  const dispatch = useAppDispatch();
  const { updateClinicUser, ensureRoomNameIsUnique }: IApiRequests = useApiRequests();

  const [loading, setLoading] = useState<boolean>(false);
  const [displayName, setDisplayName] = useState<string>(user.clinicUser.preferedDisplayName ?? '');
  const [displayNameError, setDisplayNameError] = useState('');
  const inputRef: React.RefObject<HTMLDivElement> = useRef(null);
  const schema = Yup.string().max(80, t('roomSettingsForm.maxLength80')).nullable();
  const previewAudioRef = useRef<HTMLAudioElement | null>(null);
  const previewTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [showSessionDetails, setShowSessionDetails] = useState(localStorage.getItem('hideNotesDialog') !== 'true');
  const [groupCallsEnabled, setGroupCallsEnabled] = useState(localStorage.getItem('groupCallsEnabled') === 'true');

  useEffect(() => {
    setDisplayName(user.clinicUser.preferedDisplayName ?? '');
  }, [user]);

  const handleToggleShowSessionDetails = (event: React.ChangeEvent<HTMLInputElement>) => {
    const shouldShow = event.target.checked;
    setShowSessionDetails(shouldShow);
    localStorage.setItem('hideNotesDialog', (!shouldShow).toString());
    enqueueSnackbar(t('roomSettingsForm.roomSettingUpdated'), { variant: 'success' });
  };

  const handleToggleGroupCallsEnabled = (event: React.ChangeEvent<HTMLInputElement>) => {
    const enabled = event.target.checked;

    setGroupCallsEnabled(enabled);
    localStorage.setItem('groupCallsEnabled', enabled.toString());

    enqueueSnackbar(t('roomSettingsForm.roomSettingUpdated'), { variant: 'success' });
  };

  const displayNameBtnText: string = useMemo(() => {
    return user.clinicUser.preferedDisplayName === displayName
      ? t('roomSettingsForm.edit')
      : t('roomSettingsForm.save');
  }, [user, displayName, t]);

  const handleChangeDisplayName = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    validate(value);
    setDisplayName(value);
  };

  const handleClickDisplayNameButton = useCallback(() => {
    if (user.clinicUser.preferedDisplayName !== displayName && validate(displayName)) {
      onChange({ preferedDisplayName: displayName });
      return;
    }

    if (user.clinicUser.preferedDisplayName === displayName) {
      inputRef.current!.focus();
    }
  }, [user, displayName]);

  const validate = (value?: string): boolean => {
    let isValid = false;
    try {
      schema.validateSync(value);
      setDisplayNameError('');
      isValid = true;
    } catch (e: any) {
      setDisplayNameError(e.message);
    }
    return isValid;
  };

  const onChange = useCallback(
    (data: Partial<ApiClinicUser>) => {
      updateClinicUser({ ...data })
        .then(() => dispatch(getUser()).unwrap())
        .then(() => enqueueSnackbar(t('roomSettingsForm.roomSettingUpdated'), { variant: 'success' }))
        .catch((e) => enqueueSnackbar(t('roomSettingsForm.updateClinicUserError', { error: e }), { variant: 'error' }));
    },
    [user, t],
  );

  const hadleEditRoom = (value?: string): void => {
    if (!value) {
      return;
    }

    const ensureRoomNameIsUniquePromise = (): Promise<void> => {
      return ensureRoomNameIsUnique(value ?? '').catch((error) => {
        return Promise.reject(error.message);
      });
    };

    const updateClinicUserPromise = (): Promise<void> => {
      return updateClinicUser({ roomName: value }).catch((error) => {
        return Promise.reject(error.message);
      });
    };

    ensureRoomNameIsUniquePromise()
      .then(() => updateClinicUserPromise())
      .then(() => dispatch(getUser()).unwrap())
      .then(() => enqueueSnackbar(t('roomSettingsForm.linkUpdated'), { variant: 'success' }))
      .catch((e) =>
        enqueueSnackbar(t('roomSettingsForm.updateClinicUserLinkError', { error: e }), { variant: 'error' }),
      );
  };

  const handleWaitingRoomClick = (): void => {
    navigate('../edit');
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      [event.target.name]: event.target.checked,
    });
  };

  const stopPreview = () => {
    if (previewTimerRef.current) {
      clearTimeout(previewTimerRef.current);
      previewTimerRef.current = null;
    }
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current = null;
    }
  };

  useEffect(() => () => stopPreview(), []);

  const handleSoundChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    onChange({ startCallSound: value });
    stopPreview();
    const soundFile = CALL_SOUND_FILES[value];
    if (soundFile) {
      const audio = new Audio(soundFile);
      audio.play().catch(Object);
      previewAudioRef.current = audio;
      previewTimerRef.current = setTimeout(() => {
        if (previewAudioRef.current) {
          previewAudioRef.current.pause();
          previewAudioRef.current = null;
        }
        previewTimerRef.current = null;
      }, 5000);
    }
  };

  return loading ? (
    <Spinner small />
  ) : (
    <div className={styles.form}>
      <Stack className={styles.row}>
        <Stack className={styles.titleRow}>
          <span className={styles.titleWithInfo}>
            {isRtl ? (
              <>
                <Info text={t('roomSettingsForm.roomNameUrlInfo')} />
                <Typography component='p' variant='body2'>
                  {t('roomSettingsForm.roomNameUrl')}
                </Typography>
              </>
            ) : (
              <>
                <Typography component='p' variant='body2'>
                  {t('roomSettingsForm.roomNameUrl')}
                </Typography>
                <Info text={t('roomSettingsForm.roomNameUrlInfo')} />
              </>
            )}
          </span>
        </Stack>
        <MyRoomLink type='edit' value={user.clinicUser.roomName} onClick={hadleEditRoom} className={styles.link} />
      </Stack>

      <Stack className={styles.row}>
        <Stack className={styles.titleRow}>
          <span className={styles.titleWithInfo}>
            {isRtl ? (
              <>
                <Info text={t('roomSettingsForm.preferredDisplayNameInfo')} />
                <Typography component='p' variant='body2'>
                  {t('roomSettingsForm.preferredDisplayName')}
                </Typography>
              </>
            ) : (
              <>
                <Typography component='p' variant='body2'>
                  {t('roomSettingsForm.preferredDisplayName')}
                </Typography>
                <Info text={t('roomSettingsForm.preferredDisplayNameInfo')} />
              </>
            )}
          </span>
        </Stack>
        <Stack className={styles.displayName}>
          <TextField inputRef={inputRef} value={displayName} onChange={handleChangeDisplayName} />
          <Button
            variant='contained'
            onClick={handleClickDisplayNameButton}
            disabled={displayNameError !== ''}
            className={styles.control}
          >
            {displayNameBtnText}
          </Button>
        </Stack>
        <FormErrorHelperText className={styles.errorHelper}>{displayNameError}</FormErrorHelperText>
      </Stack>

      <Stack className={styles.row}>
        <Stack className={styles.titleRow}>
          <Typography component='p' variant='body2'>
            {t('roomSettingsForm.waitingRoom')}
          </Typography>
        </Stack>
        <Button variant='contained' onClick={handleWaitingRoomClick}>
          {t('roomSettingsForm.editWaitingRoom')}
        </Button>
      </Stack>

      <Stack direction='row' spacing={1} alignItems='center' justifyContent='space-between'>
        <Stack direction={isRtl ? 'row-reverse' : 'row'} sx={{ columnGap: '0.6rem' }} alignItems='center'>
          <Typography component='p' variant='body5'>
            {t('roomSettingsForm.showSessionDetailsWhenCallEnds')}
          </Typography>
        </Stack>
        <Switch
          checked={showSessionDetails}
          name='showSessionDetails'
          inputProps={{ 'aria-label': 'ShowSessionDetails' }}
          onChange={handleToggleShowSessionDetails}
        />
      </Stack>

      <Stack direction='row' spacing={1} alignItems='center' justifyContent='space-between'>
        <Stack className={styles.switchLabelRow}>
          <span className={styles.titleWithInfo}>
            {isRtl ? (
              <>
                <Info text={t('roomSettingsForm.waitingRoomCallStartSoundInfo')} />
                <Typography component='p' variant='body5'>
                  {t('roomSettingsForm.waitingRoomCallStartSound')}
                </Typography>
              </>
            ) : (
              <>
                <Typography component='p' variant='body5'>
                  {t('roomSettingsForm.waitingRoomCallStartSound')}
                </Typography>
                <Info text={t('roomSettingsForm.waitingRoomCallStartSoundInfo')} />
              </>
            )}
          </span>
        </Stack>
        <FormControl size='small' sx={{ minWidth: 170 }}>
          <Select value={user.clinicUser.startCallSound || 'none'} onChange={handleSoundChange}>
            <MenuItem value='none'>{t('roomSettingsForm.callSoundNone')}</MenuItem>
            <MenuItem value='incoming-long'>{t('roomSettingsForm.callSoundIncomingLong')}</MenuItem>
            <MenuItem value='orbital-long'>{t('roomSettingsForm.callSoundOrbitalLong')}</MenuItem>
            <MenuItem value='steps-long'>{t('roomSettingsForm.callSoundStepsLong')}</MenuItem>
            <MenuItem value='chakra-short'>{t('roomSettingsForm.callSoundChakraShort')}</MenuItem>
            <MenuItem value='chocolate-short'>{t('roomSettingsForm.callSoundChocolateShort')}</MenuItem>
            <MenuItem value='tada-short'>{t('roomSettingsForm.callSoundTadaShort')}</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {isHipaaLink && (
        <Stack direction='row' spacing={1} alignItems='center' justifyContent='space-between'>
          <Stack className={styles.switchLabelRow}>
            <span className={styles.titleWithInfo}>
              {isRtl ? (
                <>
                  <Info text={t('promptAiNotesAtCallStartInfo', { ns: 'providerRoom' })} />
                  <Typography component='p' variant='body5'>
                    {t('promptAiNotesAtCallStart', { ns: 'providerRoom' })}
                  </Typography>
                </>
              ) : (
                <>
                  <Typography component='p' variant='body5'>
                    {t('promptAiNotesAtCallStart', { ns: 'providerRoom' })}
                  </Typography>
                  <Info text={t('promptAiNotesAtCallStartInfo', { ns: 'providerRoom' })} />
                </>
              )}
            </span>
          </Stack>
          <Switch
            checked={user.clinicUser.autoStartPromptAINotes}
            name='autoStartPromptAINotes'
            inputProps={{ 'aria-label': 'AutoStartPromptAINotes' }}
            onChange={handleChange}
          />
        </Stack>
      )}

      <Stack direction='row' spacing={1} alignItems='center' justifyContent='space-between'>
        <Stack className={styles.switchLabelRow}>
          <span className={styles.titleWithInfo}>
            {isRtl ? (
              <>
                <Info text={t('roomSettingsForm.groupCallsInfo')} />
                <Typography component='p' variant='body5'>
                  {t('roomSettingsForm.groupCalls')}
                </Typography>
              </>
            ) : (
              <>
                <Typography component='p' variant='body5'>
                  {t('roomSettingsForm.groupCalls')}
                </Typography>
                <Info text={t('roomSettingsForm.groupCallsInfo')} />
              </>
            )}
          </span>
        </Stack>
        <Switch
          checked={groupCallsEnabled}
          name='groupCallsEnabled'
          inputProps={{ 'aria-label': 'GroupCallsEnabled' }}
          onChange={handleToggleGroupCallsEnabled}
        />
      </Stack>

      <Stack direction='row' spacing={1} alignItems='center' justifyContent='space-between'>
        <Stack className={styles.switchLabelRow}>
          <span className={styles.titleWithInfo}>
            {isRtl ? (
              <>
                <Info text={t('roomSettingsForm.unavailableInfo')} />
                <Typography component='p' variant='body5'>
                  {t('roomSettingsForm.unavailable')}
                </Typography>
              </>
            ) : (
              <>
                <Typography component='p' variant='body5'>
                  {t('roomSettingsForm.unavailable')}
                </Typography>
                <Info text={t('roomSettingsForm.unavailableInfo')} />
              </>
            )}
          </span>
        </Stack>
        <Switch
          checked={user.clinicUser.unavailable}
          name='unavailable'
          inputProps={{ 'aria-label': 'Unavailable' }}
          onChange={handleChange}
        />
      </Stack>
    </div>
  );
}
